import PropTypes from 'prop-types';
import React from 'react';

import { IconArrowRight } from '$partials/icons/index.js';
import { Image } from '$partials/media/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof cardPropTypes>>}
 */
const CardItem = ({
    body,
    cardTitle,
    ctaLabel,
    ctaLink,
    eyebrowIcon,
    eyebrowTitle,
    image,
    imageGreyOutline
}) => {
    const { formatURI } = useLocale();

    return (
        <div className='flex flex-col justify-between rounded-button bg-core-sand'>
            <div className='p-40'>
                {(eyebrowIcon || eyebrowTitle) && (
                    <div className='flex items-center gap-10 mb-22'>
                        {eyebrowIcon && (
                            <div
                                className='pointer-events-none select-none'
                                style={{
                                    fontSize: '18px',
                                    maxWidth: '100%',
                                    width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em`
                                }}>
                                <Image
                                    {...eyebrowIcon}
                                    loading={''} />
                            </div>
                        )}

                        {eyebrowTitle && (
                            <p className='type-style-7 text-core-brand'>
                                {eyebrowTitle}
                            </p>
                        )}
                    </div>
                )}

                {cardTitle && (
                    <h3 className='type-style-3 text-grey-black'>
                        {cardTitle}
                    </h3>
                )}

                {body && (
                    <p className='mt-4 type-style-8 text-grey-off-black '>
                        {body}
                    </p>
                )}

                {(ctaLabel && ctaLink) && (
                    <p className='mt-24'>
                        <a
                            className='button button--tertiary-large'
                            href={formatURI(ctaLink)}>
                            <span>{ctaLabel}</span>
                            <span className='button__icon'>
                                <IconArrowRight />
                            </span>
                        </a>
                    </p>
                )}
            </div>

            {image?.contentType && (
                <div className='pt-20 pointer-events-none select-none'>
                    {
                        (image.contentType?.includes('video/')) ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                src={image?.src}
                                width={'100%'} />
                        ) : (
                            <Image
                                {...image}
                                className={imageGreyOutline ? 'border border-grey' : ''}
                                loadWidth={840}
                                style={{ width: undefined }} />
                        )
                    }
                </div>
            )}
        </div>
    );
};

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const ModuleCards = ({
    cards,
    parent,
    title
}) => {
    const items = cards?.items;

    if (!items.length) { return null; }

    return (
        <div className={`bg-grey-white ${parent === 'Home' ? 'py-40 md:py-50' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>
                {title && (
                    <h2 className='px-10 sm:px-5 md:px-10 type-style-2 mb-20 md:mb-60'>
                        {title}
                    </h2>
                )}

                <div className={`px-10 sm:px-5 md:px-10 grid sm:grid-cols-2 ${items.length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-20 sm:gap-10 md:gap-20`}>
                    {items.map((item, i) => item ?
                        <CardItem
                            key={i}
                            {...item} />
                        : null
                    ).filter(Boolean)}
                </div>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const cardPropTypes = {
    body: PropTypes.string,
    cardTitle: PropTypes.string,
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    eyebrowTitle: PropTypes.string,
    eyebrowIcon: PropTypes.shape({
        height: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired
    }),
    image: PropTypes.shape({
        src: PropTypes.string.isRequired
    }),
    imageGreyOutline: PropTypes.bool,
};

CardItem.propTypes = cardPropTypes;

const propTypes = {
    cards: PropTypes.object,
    parent: PropTypes.string,
    title: PropTypes.string
};

ModuleCards.propTypes = propTypes;

export default ModuleCards;
